import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { Box, Typography } from "@mui/material";
import SortableBannerCard from "./SortableBannerCard";
import { ReordenItem } from "./reorden.types";

interface Props {
  items: ReordenItem[];
  onChange: (items: ReordenItem[]) => void;
}

/**
 * Zona A — Reordenamiento. Contenedor drag-and-drop del carrusel.
 * Funciona con mouse, touch y teclado (PointerSensor / TouchSensor / KeyboardSensor).
 * Al soltar, recalcula el `orden` (1..N) por posicion y lo notifica al padre.
 */
const CarruselReorder: React.FC<Props> = ({ items, onChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.idDistribucion === active.id);
    const newIndex = items.findIndex((i) => i.idDistribucion === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordenados = arrayMove(items, oldIndex, newIndex).map(
      (it, idx) => ({ ...it, orden: idx + 1 })
    );
    onChange(reordenados);
  };

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No hay banners en este carrusel.
      </Typography>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((i) => i.idDistribucion)}
        strategy={verticalListSortingStrategy}
      >
        <Box>
          {items.map((item, idx) => (
            <SortableBannerCard
              key={item.idDistribucion}
              item={item}
              posicion={idx + 1}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default CarruselReorder;
